import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoaderBar } from 'components'
import { useAuth } from 'context/user'
import { useNotification } from 'hooks/toast'
import { GetNotificationGroupCustomerList } from 'hooks'
import { HomeDashboardService } from 'utility/services/home-dashboard'
import {
    CLASSIC_DASHBOARD_PATH,
    isHomeDashboardAdmin,
    isHomeDashboardEligible,
} from 'utility/home-dashboard'
import { HomeDashboardContainer } from 'page-components/home-dashboard/styles'
import { LeadershipView } from 'page-components/home-dashboard/LeadershipView'
import { AuditorView } from 'page-components/home-dashboard/AuditorView'
import { useHomeDashboardNavigation } from 'page-components/home-dashboard/navigation'
import { makeUnit } from 'page-components/home-dashboard/format'

const RANGE_KEY = 'eauditor.homeDashboard.range'

const readRange = () => {
    try {
        const v = Number(sessionStorage.getItem(RANGE_KEY))
        return v === 7 || v === 30 || v === 90 ? v : 30
    } catch {
        return 30
    }
}

// Customer list only for the leadership view (the filter in the hero band).
const LeadershipWithCustomers = props => {
    const { options } = GetNotificationGroupCustomerList(props.user?.CompanyId)
    const customers = useMemo(
        () =>
            (options || []).filter(
                // 23 = the global "Self" placeholder customer (company 0): not a customer of this company
                o => o && o.value !== undefined && o.value !== null && Number(o.value) !== 23 && o.text,
            ),
        [options],
    )
    return <LeadershipView {...props} customers={customers} />
}

// GET Dashboard/Home. A company still on the classic layout (the default) is sent straight to the
// existing task dashboard, so nothing changes for it. Cintas and franchise users never get here.
export const HomeDashboardScreen = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const nav = useHomeDashboardNavigation()
    const { errorToast } = useNotification()
    // a ref: useNotification returns new functions on every render (a dependency would reload in a loop)
    const errorToastRef = useRef(errorToast)
    errorToastRef.current = errorToast
    const eligible = isHomeDashboardEligible(user)
    const [range, setRange] = useState(readRange)
    const [customerId, setCustomerId] = useState(null)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const requestId = useRef(0)

    const load = useCallback(async () => {
        requestId.current += 1
        const id = requestId.current
        setLoading(true)
        setError('')
        try {
            const res = await HomeDashboardService.getHome({ range, customerId })
            if (id !== requestId.current) return
            if (!res || res.Layout !== 'home') {
                navigate(CLASSIC_DASHBOARD_PATH, { replace: true })
                return
            }
            setData(res)
        } catch (err) {
            if (id !== requestId.current) return
            if (err?.status === 400 && customerId) {
                // customer no longer valid for this company: fall back to all customers
                errorToastRef.current('That customer is not available here. Showing all customers.')
                setCustomerId(null)
                return
            }
            // never leave numbers of another period / customer on screen under the new selection
            setData(null)
            setError('The dashboard could not be loaded. Please try again, or open the task list.')
        } finally {
            if (id === requestId.current) setLoading(false)
        }
    }, [range, customerId, navigate])

    useEffect(() => {
        if (!eligible) {
            // franchise users have their own landing page; Cintas keeps the task list
            const fallback =
                Number(user?.DashboardStyle?.DashboardId) === 2 ? '/app/dashboard/groups' : CLASSIC_DASHBOARD_PATH
            navigate(fallback, { replace: true })
            return
        }
        load()
    }, [eligible, load, navigate, user])

    const onRange = r => {
        setRange(r)
        try {
            sessionStorage.setItem(RANGE_KEY, String(r))
        } catch {
            // ignore storage errors
        }
    }

    if (!eligible) return null

    const { unit, Unit } = makeUnit(data?.UnitWord)
    const canEdit = isHomeDashboardAdmin(user)

    return (
        <HomeDashboardContainer>
            {loading && <LoaderBar content="Loading dashboard" />}
            {error && !data && (
                <div className="notice bad">
                    {error}{' '}
                    <button type="button" className="hd-link" onClick={load}>
                        Retry
                    </button>{' '}
                    ·{' '}
                    <button type="button" className="hd-link" onClick={nav.openTasks}>
                        Task list
                    </button>
                </div>
            )}
            {error && data && <div className="notice bad">{error}</div>}
            {data && data.View === 'leadership' && (
                <LeadershipWithCustomers
                    data={data}
                    user={user}
                    range={range}
                    onRange={onRange}
                    customerId={customerId}
                    onCustomer={setCustomerId}
                    nav={nav}
                    canEdit={canEdit}
                    unit={unit}
                    unitTitle={Unit}
                />
            )}
            {data && data.View !== 'leadership' && (
                <AuditorView data={data} nav={nav} canEdit={canEdit} unit={unit} unitTitle={Unit} />
            )}
        </HomeDashboardContainer>
    )
}
